import { ETHIC_TEAM_LABEL, MED_ERROR_INCIDENT_LABEL } from './complianceCategoryTypes';
import {
  canAccessNurseNotesTeamDashboard,
  canViewDepartmentScopedIncidentReportNav,
  canViewEthicTeamSection,
  isPrivilegedIncidentNavRole,
} from './privilegedIncidentNavRole';
import { hasTeamCategoryHospitalWideAccess } from './reportingTeamCategoryScope';
import type { RoleContext } from './roles';
import { canViewOrgClimateSurveys } from './orgClimateSurveys/access';

/** Exactly one navigation target — TypeScript rejects `{ to, href }` together. */
export type ShellNavLink = {
  key: string;
  label: string;
} & ({ to: string; href?: never } | { href: string; to?: never });

export const TEAM_DESTINATION_NN = {
  key: 'nn',
  path: '/report?category=nurse_notes',
  label: 'บันทึกทางการพยาบาล',
} as const;

export const TEAM_DESTINATION_ETHIC = {
  key: 'ethic',
  path: '/report?category=ethicSurveys',
  label: ETHIC_TEAM_LABEL,
} as const;

export const TEAM_DESTINATION_ORG_CLIMATE = {
  key: 'org_climate',
  path: '/report?category=orgClimateSurveys',
  label: 'บรรยากาศจริยธรรมในองค์การพยาบาล',
} as const;

export const TEAM_DESTINATION_SIREN = {
  key: 'siren',
  path: '/report?category=siren_bundle',
  label: 'SIREN bundle',
} as const;

export const TEAM_DESTINATION_MED_ERROR = {
  key: 'med_error_incident',
  path: '/med-error-incidents',
  label: 'รายงานอุบัติการณ์ความคลาดเคลื่อนทางยา',
} as const;

export const TEAM_DESTINATION_FALL = {
  key: 'fall_incident',
  path: '/fall-incidents',
  label: 'จำนวนอุบัติการณ์การพลัดตกหกล้ม',
} as const;

export const TEAM_DESTINATION_KPOA = {
  key: 'kpoa',
  path: '/kpoa-incidents',
  label: 'คณะทำงานคปอ.',
} as const;

export const TEAM_DESTINATION_TG = {
  key: 'tg',
  path: '/team/occ',
  label: 'โครงการ TG',
} as const;

const PRODUCTIVITY_LOOKER_DASHBOARD_URL =
  'https://lookerstudio.google.com/reporting/d53207d9-1bfc-4d58-8fae-83363d3c72bb/page/pyJaF' as const;

export function buildExportNavLinks(input: { canOpenExportHub: boolean }): ShellNavLink[] {
  if (!input.canOpenExportHub) return [];
  return [{ key: 'export', label: 'ส่งออก', to: '/report?tab=export' }];
}

/**
 * Single ethic-visibility rule for shell team + report nav.
 * Ethic team membership/privileged roles OR department-scoped incident report access.
 */
export function canSeeEthicShellNav(
  roleContext: RoleContext | null,
  canSeeDepartmentScoped: boolean,
): boolean {
  return canViewEthicTeamSection(roleContext) || canSeeDepartmentScoped;
}

export function buildTeamNavLinks(input: {
  roleContext: RoleContext | null;
  isAdmin: boolean;
  canViewOrgClimate: boolean;
  hasTGProjectAccess: boolean;
  includeKpoa: boolean;
}): ShellNavLink[] {
  const {
    roleContext,
    isAdmin,
    canViewOrgClimate,
    hasTGProjectAccess,
    includeKpoa,
  } = input;
  const links: ShellNavLink[] = [];
  const privilegedNav = isPrivilegedIncidentNavRole(roleContext);
  const canSeeDepartmentScoped = canViewDepartmentScopedIncidentReportNav(roleContext);
  const canSeeEthicReport = canSeeEthicShellNav(roleContext, canSeeDepartmentScoped);

  if (canSeeDepartmentScoped || canAccessNurseNotesTeamDashboard(roleContext, isAdmin)) {
    links.push({
      key: TEAM_DESTINATION_NN.key,
      label: TEAM_DESTINATION_NN.label,
      to: TEAM_DESTINATION_NN.path,
    });
  }
  if (canSeeEthicReport) {
    links.push({
      key: TEAM_DESTINATION_ETHIC.key,
      label: TEAM_DESTINATION_ETHIC.label,
      to: TEAM_DESTINATION_ETHIC.path,
    });
  }
  if (canViewOrgClimate) {
    links.push({
      key: TEAM_DESTINATION_ORG_CLIMATE.key,
      label: TEAM_DESTINATION_ORG_CLIMATE.label,
      to: TEAM_DESTINATION_ORG_CLIMATE.path,
    });
  }
  if (privilegedNav) {
    links.push({
      key: TEAM_DESTINATION_MED_ERROR.key,
      label: TEAM_DESTINATION_MED_ERROR.label,
      to: TEAM_DESTINATION_MED_ERROR.path,
    });
    links.push({
      key: TEAM_DESTINATION_FALL.key,
      label: TEAM_DESTINATION_FALL.label,
      to: TEAM_DESTINATION_FALL.path,
    });
    if (includeKpoa) {
      links.push({
        key: TEAM_DESTINATION_KPOA.key,
        label: TEAM_DESTINATION_KPOA.label,
        to: TEAM_DESTINATION_KPOA.path,
      });
    }
  }
  if (hasTGProjectAccess) {
    links.push({
      key: TEAM_DESTINATION_TG.key,
      label: TEAM_DESTINATION_TG.label,
      to: TEAM_DESTINATION_TG.path,
    });
  }
  return links;
}

export function buildReportNavLinks(input: {
  roleContext: RoleContext | null;
  isAdmin: boolean;
  uid?: string | null;
  canSeeDepartmentScopedIncidentReports: boolean;
  canSeeEthicReportNav: boolean;
  hasMedErrorIncidentAccess: boolean;
  hasFallIncidentAccess: boolean;
  hasKpoaIncidentAccess: boolean;
}): ShellNavLink[] {
  const {
    roleContext,
    isAdmin,
    uid,
    canSeeDepartmentScopedIncidentReports,
    canSeeEthicReportNav,
    hasMedErrorIncidentAccess,
    hasFallIncidentAccess,
    hasKpoaIncidentAccess,
  } = input;
  const links: ShellNavLink[] = [];
  const showNurseNotes =
    canSeeDepartmentScopedIncidentReports ||
    canAccessNurseNotesTeamDashboard(roleContext, isAdmin);

  if (showNurseNotes) {
    links.push({
      key: 'report-nurse-notes',
      label: TEAM_DESTINATION_NN.label,
      to: TEAM_DESTINATION_NN.path,
    });
  }
  if (
    canSeeDepartmentScopedIncidentReports ||
    hasTeamCategoryHospitalWideAccess(roleContext, 'siren_bundle')
  ) {
    links.push({
      key: 'report-siren',
      label: TEAM_DESTINATION_SIREN.label,
      to: TEAM_DESTINATION_SIREN.path,
    });
  }
  if (canSeeEthicReportNav) {
    links.push({
      key: 'report-ethic',
      label: TEAM_DESTINATION_ETHIC.label,
      to: TEAM_DESTINATION_ETHIC.path,
    });
  }
  if (canViewOrgClimateSurveys(uid)) {
    links.push({
      key: 'report-org-climate',
      label: TEAM_DESTINATION_ORG_CLIMATE.label,
      to: TEAM_DESTINATION_ORG_CLIMATE.path,
    });
  }
  if (hasMedErrorIncidentAccess) {
    links.push({
      key: 'report-med-error',
      label: MED_ERROR_INCIDENT_LABEL,
      to: '/med-error-incidents',
    });
  }
  if (hasFallIncidentAccess) {
    links.push({
      key: 'report-fall',
      label: 'รายงานอุบัติการณ์ Fall',
      to: '/fall-incidents',
    });
  }
  if (hasKpoaIncidentAccess) {
    links.push({
      key: 'report-kpoa',
      label: 'รายงานอุบัติการณ์จากการทำงาน',
      to: '/kpoa-incidents',
    });
  }
  links.push({
    key: 'report-hr',
    label: 'ทรัพยากรบุคคล',
    to: '/hr',
  });
  links.push({
    key: 'report-safety-culture-2569',
    label: 'วัฒนธรรมความปลอดภัย ปี 2569',
    to: '/safety-culture-2569',
  });
  links.push({
    key: 'report-productivity',
    label: 'Productivity Dashboard',
    href: PRODUCTIVITY_LOOKER_DASHBOARD_URL,
  });
  return links;
}
